// Panel de edición de video con IA (Gemini Omni Flash).
//
// Feature NUEVA e independiente del editor de Sora (`editar_vid`/`EditarVideo`):
// NO lo reemplaza. Corre el flujo async de `omniEditVideo`. Solo clips ≤10s.
// Ver ADR-500.
//
// Soporta 2 layouts (prop `layout`):
//   • 'panel'  (default) → vertical: VIDEO arriba llenando el espacio + COMPOSER
//     abajo. Se usa en la SIDEBAR izquierda del editor de video (timeline).
//   • 'page'   → horizontal: VIDEO a la IZQUIERDA (grande) + COMPOSER en una card
//     a la DERECHA. Stackea en mobile. Se usa en la PÁGINA del historial
//     (espeja el look de "Editar imagen"). Ver ADR-502.
//
// El componente NO persiste: al Guardar llama `onSave(result, mode)` y el
// contenedor decide qué hacer (reemplazar el clip vs sumar asset). El
// header/cierre los provee el contenedor.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Sparkles,
  Undo2,
  Redo2,
  Save,
  Loader2,
  Check,
  X,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  omniEditVideo,
  creditsForOmniEdit,
  type OmniAspectRatio,
} from '../lib/omniEdit';
import { OmniRefAttach } from './OmniRefAttach';
import { SendCreditsButton } from './SendCreditsButton';
import {
  signReferenceImage,
  type ChatAttachment,
} from '../pages/v2/agentAttachments';

export interface EditarVideoIASaveResult {
  /** Signed URL del video editado elegido. */
  url: string;
  /** Storage path del video editado en `video-project-assets`. */
  path: string;
}

export interface EditarVideoIAProps {
  /** Signed URL (descargable) del clip original a editar. */
  clipUrl: string;
  /** Storage path del asset original (para "Guardar" = reemplazar). Opcional. */
  originalPath?: string;
  /** Duración del clip en segundos (≤10). Define el costo en créditos. */
  durationSec: number;
  aspectRatio: OmniAspectRatio;
  projectId: string;
  companyId: string;
  /**
   * Se llama al Guardar. `mode`:
   *  - 'replace' → reemplazar el clip original por el editado.
   *  - 'copy'    → conservar el original y sumar el editado como nuevo.
   */
  onSave: (
    result: EditarVideoIASaveResult,
    mode: 'replace' | 'copy',
  ) => void | Promise<void>;
  /** Cierra el panel (el contenedor lo mapea a setActivePanel('none')). */
  onClose: () => void;
  /**
   * Si es `true`, muestra el video ORIGINAL desde el arranque (como el flujo de
   * "editar imagen"). Si es `false` (default, timeline), arriba queda un
   * placeholder en blanco hasta que se genera la primera edición.
   */
  showOriginalPreview?: boolean;
  /** Texto secundario del ítem "Guardar" del menú (según el contexto). */
  replaceHint?: string;
  /** Texto secundario del ítem "Guardar como copia" del menú. */
  copyHint?: string;
  /**
   * Si es `true`, muestra UN SOLO botón "Guardar" (sin el split "Guardar como
   * copia" / reemplazar). Usado en el historial, donde NUNCA se reemplaza ni
   * borra el video original: siempre se guarda como item nuevo.
   */
  singleSave?: boolean;
  /**
   * Layout del componente:
   *  - 'panel' (default) → vertical (video arriba, composer abajo) para la
   *    sidebar del editor de video.
   *  - 'page' → horizontal (video izquierda, composer en card a la derecha;
   *    stackea en mobile) para la página del historial. Espeja "Editar imagen".
   */
  layout?: 'panel' | 'page';
}

type Version = { url: string; path?: string };

export const EditarVideoIA: React.FC<EditarVideoIAProps> = ({
  clipUrl,
  originalPath,
  durationSec,
  aspectRatio,
  projectId,
  companyId,
  onSave,
  onClose,
  showOriginalPreview = false,
  singleSave = false,
  layout = 'panel',
}) => {
  const [instruction, setInstruction] = useState('');
  // Imagen de referencia OPCIONAL (subida o producto del catálogo) para guiar la
  // edición. Una sola; agregar otra reemplaza. Vacía = edición solo con texto
  // (idéntico al comportamiento previo).
  const [refImage, setRefImage] = useState<ChatAttachment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEditSuccess, setShowEditSuccess] = useState(false);
  const [history, setHistory] = useState<Version[]>([
    { url: clipUrl, path: originalPath },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [showInsufficient, setShowInsufficient] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const credits = creditsForOmniEdit(durationSec);
  const hasEdits = currentIndex > 0;
  const currentUrl = history[currentIndex]?.url || clipUrl;
  // Mostramos un <video> real cuando ya hay una edición, o cuando el contenedor
  // pidió previsualizar el original (historial). Si no, placeholder en blanco.
  const showVideo = hasEdits || (showOriginalPreview && !!currentUrl);
  // El <video> se muestra a su TAMAÑO NATURAL (sin forzar aspect ni object-cover)
  // → el elemento ES la relación real del clip, así que NUNCA hay barras negras
  // (16:9, 9:16, 1:1 o lo que sea). Solo lo acotamos por tamaño. En 'page' lo
  // achicamos (max 70vh / 46vw) para que quede balanceado con el card.
  const videoClass =
    layout === 'page'
      ? // Mobile: llena el área disponible (contenido, sin barras vía aspect).
        // Desktop: h-full para igualar la altura del card.
        'max-h-full max-w-full md:h-full md:w-auto md:max-w-[46vw] object-contain rounded-lg'
      : 'max-h-full max-w-full rounded-lg';
  // En 'page' fijamos el aspect-ratio inline: con height fijo + width auto el
  // <video> nace con el ancho correcto y llena la altura del card SIN barras
  // (mismo truco que "Editar imagen"; sirve para 16:9, 9:16 y 1:1).
  const videoStyle =
    layout === 'page'
      ? {
          aspectRatio:
            aspectRatio === '16:9'
              ? '16 / 9'
              : aspectRatio === '1:1'
                ? '1 / 1'
                : '9 / 16',
        }
      : undefined;

  // Reset al cambiar de clip (el panel se re-monta con otro clipUrl).
  useEffect(() => {
    setHistory([{ url: clipUrl, path: originalPath }]);
    setCurrentIndex(0);
    setInstruction('');
    setRefImage(null);
    setError(null);
    setShowEditSuccess(false);
  }, [clipUrl, originalPath]);

  useEffect(() => {
    if (!showEditSuccess) return;
    const t = window.setTimeout(() => setShowEditSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [showEditSuccess]);

  const checkCredits = async (): Promise<boolean> => {
    try {
      const { data: cp } = await supabase
        .from('company_profiles')
        .select('imagenes_restantes')
        .eq('id_company', companyId)
        .single();
      const rem = (cp as any)?.imagenes_restantes as number | undefined;
      if (typeof rem === 'number' && rem < credits) {
        setRemaining(rem);
        setShowInsufficient(true);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  const handleSend = async () => {
    const trimmed = instruction.trim();
    if (!trimmed || isSubmitting) return;
    if (!(await checkCredits())) return;
    setIsSubmitting(true);
    setError(null);
    setShowEditSuccess(false);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se pudo identificar al usuario.');
      // Si hay imagen de referencia: la subida se re-firma (los signed URL
      // expiran); el producto ya trae una URL usable. Vacío/undefined = edición
      // solo con texto (el flujo n8n cae en la rama "solo texto").
      let referenceImageUrl: string | undefined;
      if (refImage) {
        referenceImageUrl =
          refImage.kind === 'image'
            ? (await signReferenceImage(
                refImage.storagePath,
                refImage.bucket,
              )) ?? undefined
            : refImage.imageUrl || undefined;
        // Adjuntó una imagen pero no se pudo preparar (firma falló / URL vacía):
        // NO seguir en silencio como edición de solo texto — avisar (lo captura
        // el catch → banner de error), así el usuario no cree que la usó.
        if (!referenceImageUrl) {
          throw new Error(
            'No se pudo preparar la imagen de referencia. Quitala y adjuntala de nuevo.',
          );
        }
      }
      const result = await omniEditVideo({
        userId: user.id,
        companyId,
        projectId,
        clipUrl: currentUrl,
        instruction: trimmed,
        aspectRatio,
        durationSec,
        referenceImageUrl,
      });
      setHistory((prev) => {
        const base = prev.slice(0, currentIndex + 1);
        return [...base, { url: result.signedUrl, path: result.path }];
      });
      setCurrentIndex((prev) => prev + 1);
      setInstruction('');
      setShowEditSuccess(true);
    } catch (e: any) {
      setError(e?.message || 'Ocurrió un error al editar el video.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUndo = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };
  const handleRedo = () => {
    if (currentIndex < history.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const doSave = async (mode: 'replace' | 'copy') => {
    const cur = history[currentIndex];
    if (!cur?.path) {
      setError('No se pudo guardar: el video editado no está listo.');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ url: cur.url, path: cur.path }, mode);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar el video.');
    } finally {
      setIsSaving(false);
    }
  };

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
    el.style.overflowY = el.scrollHeight > 132 ? 'auto' : 'hidden';
  };

  // ── Piezas compartidas por ambos layouts ─────────────────────────────────
  // Contenido del área de video: badge de estado + el <video> (o el placeholder
  // en blanco). El WRAPPER (tamaño + marco) lo pone cada layout.
  const videoInner = (
    <>
      {(isSubmitting || showEditSuccess) && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                <span className="text-[13px] font-medium text-gray-700">
                  Editando video…
                </span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-[13px] font-medium text-gray-700">
                  Video editado
                </span>
              </>
            )}
          </div>
        </div>
      )}
      {showVideo ? (
        // Tamaño natural → nunca hay barras negras, sea cual sea el formato.
        <video
          key={currentUrl}
          src={currentUrl}
          controls
          playsInline
          className={videoClass}
          style={videoStyle}
        >
          Tu navegador no soporta la reproducción de video.
        </video>
      ) : (
        // Sin editar todavía → placeholder en blanco. El video editado aparece
        // acá al generar (como el flujo de editar imagen).
        <div className="flex flex-col items-center gap-2 text-center px-6">
          <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-brand-primary" />
          </div>
          <p className="text-sm font-medium text-gray-700">
            El video editado va a aparecer acá
          </p>
          <p className="text-xs text-gray-500">
            Describí el cambio y Atlas lo aplica.
          </p>
        </div>
      )}
    </>
  );

  // Marco del área de video: sin marco cuando hay video; cuadrado gris solo en
  // el estado placeholder.
  const videoBoxClass = `relative flex items-center justify-center ${
    showVideo
      ? ''
      : 'rounded-xl border border-black/[0.06] overflow-hidden bg-gray-50'
  }`;

  const errorBanner = error ? (
    <div className="bg-red-50 border border-red-200 px-3 py-2 rounded-md shrink-0">
      <p className="text-[13px] font-medium text-red-600">No se pudo editar</p>
      <p className="text-[13px] text-red-600 mt-0.5">{error}</p>
    </div>
  ) : null;

  // Composer (prompt + pill de créditos + enviar) + hint + fila Deshacer/
  // Rehacer/Guardar. Igual en ambos layouts.
  const composerBlock = (
    <div className="flex flex-col gap-2 shrink-0">
      <div
        className={`flex flex-col bg-gray-50 border border-gray-300 rounded-3xl ${
          isSubmitting ? 'shine-effect' : ''
        } focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-primary`}
      >
        {/* Chip de la imagen de referencia (subida o producto): preview + quitar. */}
        {refImage && (
          <div className="flex items-center gap-2 px-3 pt-3">
            <div className="relative shrink-0">
              <img
                src={
                  refImage.kind === 'image'
                    ? refImage.previewUrl
                    : refImage.imageUrl || ''
                }
                alt="Imagen de referencia"
                className="w-12 h-12 rounded-lg object-cover border border-black/10 bg-gray-100"
              />
              <button
                type="button"
                onClick={() => setRefImage(null)}
                aria-label="Quitar imagen de referencia"
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center shadow"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <span className="text-[12px] text-gray-500 truncate max-w-[160px]">
              {refImage.kind === 'product'
                ? refImage.name
                : 'Imagen de referencia'}
            </span>
          </div>
        )}
        <div className="relative min-h-[44px]">
          <textarea
            ref={inputRef}
            value={instruction}
            onChange={(e) => {
              setInstruction(e.target.value);
              autoResize(e.target);
            }}
            placeholder="Describí el cambio que querés hacer…"
            disabled={isSubmitting}
            rows={1}
            className="w-full pl-10 pr-4 bg-transparent focus:outline-none border-none text-sm placeholder:text-gray-400 disabled:text-gray-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
            style={{
              lineHeight: '1.25rem',
              paddingTop: '0.75rem',
              paddingBottom: '0.5rem',
            }}
          />
          <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
            <Sparkles className="w-4 h-4" aria-hidden="true" />
          </span>
        </div>
        <div className="flex items-center justify-between px-2 pb-2">
          <div className="flex items-center gap-2">
            {/* "+" de imagen de referencia (subir imagen / elegir producto) —
                mismo patrón/responsive que el del asistente. */}
            <OmniRefAttach
              companyId={companyId}
              value={refImage}
              onChange={setRefImage}
              disabled={isSubmitting}
            />
          </div>
          {/* Enviar con los créditos DENTRO del botón (píldora) — homogéneo con
              el editor de imagen. */}
          <SendCreditsButton
            credits={credits}
            onClick={handleSend}
            disabled={isSubmitting || !instruction.trim()}
            ariaLabel="Enviar instrucción"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Pedí cambios genéricos (estilo, fondo, color). Los detalles muy chicos
        pueden no aplicarse bien. Máx. 10 segundos.
      </p>

      {/* Deshacer / Rehacer / Guardar — solo tras ≥1 edición */}
      {history.length > 1 && (
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            onClick={handleUndo}
            disabled={currentIndex <= 0 || isSaving}
            className="flex-1 h-10 px-2 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-1.5"
          >
            <Undo2 className="w-4 h-4" />
            <span className="text-[13px]">Deshacer</span>
          </button>
          <button
            type="button"
            onClick={handleRedo}
            disabled={currentIndex >= history.length - 1 || isSaving}
            className="flex-1 h-10 px-2 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-1.5"
          >
            <Redo2 className="w-4 h-4" />
            <span className="text-[13px]">Rehacer</span>
          </button>

          {/* Un solo botón "Guardar". En timeline reemplaza el clip PERO
              conserva el original (el editado se registra como asset nuevo en
              Archivos y no se borra el original). En el historial guarda como
              item nuevo (nunca toca el original). */}
          <button
            type="button"
            onClick={() => doSave(singleSave ? 'copy' : 'replace')}
            disabled={isSaving || !hasEdits}
            className="flex-1 h-10 px-2 bg-brand-primary hover:bg-brand-primary-hover disabled:bg-brand-primary/40 disabled:cursor-not-allowed text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-1.5"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="text-[13px]">Guardar</span>
          </button>
        </div>
      )}
    </div>
  );

  // Modal de créditos insuficientes (overlay) — común a ambos layouts.
  const insufficientModal = showInsufficient
    ? createPortal(
        <div className="fixed inset-0 bg-brand-black/30 z-[80] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-lg p-6 relative text-center">
            <button
              type="button"
              onClick={() => setShowInsufficient(false)}
              className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Créditos insuficientes
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              Para editar este video necesitás{' '}
              <span className="font-semibold">{credits} créditos</span>, pero
              solo tenés{' '}
              <span className="font-semibold">{remaining} disponibles</span>.
            </p>
            <button
              type="button"
              onClick={() => setShowInsufficient(false)}
              className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full text-sm font-medium"
            >
              Entendido
            </button>
          </div>
        </div>,
        document.body,
      )
    : null;

  // ── Layout 'page' (historial): video + card COMPACTA, CENTRADOS y juntos en
  // el medio (video más chico → balanceado, sin el hueco de estirar a lo alto).
  if (layout === 'page') {
    return (
      <div className="flex flex-col md:flex-row md:items-stretch md:justify-center h-full gap-4 md:gap-8 p-4 overflow-hidden">
        {/* Video: en MOBILE llena el medio (flex-1, como "Editar imagen"); en
            DESKTOP se ajusta a su ancho con altura completa (md:h-full). */}
        <div className="relative flex-1 min-h-0 md:flex-none md:w-auto md:h-full flex items-center justify-center">
          {videoInner}
        </div>
        {/* Composer: en MOBILE va PLANO abajo (sin card ni encabezado, igual que
            "Editar imagen"); en DESKTOP es un card alto (header arriba + composer
            al fondo). Las clases de card son `md:`-only. */}
        <div className="w-full flex-shrink-0 md:w-[400px] md:h-full md:shrink-0 md:bg-surface-card md:rounded-2xl md:border md:border-neutral-200 md:p-5 flex flex-col">
          {/* Encabezado — solo desktop (en mobile ocupa lugar y queda feo).
              `md:flex-1 + justify-center` lo centra verticalmente en el card (el
              composer queda abajo). */}
          <div className="hidden md:flex md:flex-1 md:flex-col md:justify-center text-center px-2">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-primary/10 flex items-center justify-center mb-3">
              <Sparkles className="w-7 h-7 text-brand-primary" />
            </div>
            <p className="text-[16px] font-semibold text-brand-black/90">
              Editá el video con IA
            </p>
            <p className="text-[13px] text-gray-500 mt-1">
              Describí con texto el cambio que querés hacer y Atlas lo aplica.
            </p>
          </div>
          {/* Composer al fondo (desktop `mt-auto`; en mobile pegado al video). */}
          <div className="flex flex-col gap-2 pt-3 md:mt-auto md:pt-6">
            {errorBanner}
            {composerBlock}
          </div>
        </div>
        {insufficientModal}
      </div>
    );
  }

  // ── Layout 'panel' (default, timeline): video ARRIBA + composer ABAJO ─────
  return (
    <div className="flex flex-col h-full p-3 gap-3">
      <div className={`flex-1 min-h-[180px] ${videoBoxClass}`}>{videoInner}</div>
      {errorBanner}
      {composerBlock}
      {insufficientModal}
    </div>
  );
};

export default EditarVideoIA;
